import { Alert, Snackbar } from '@mui/material';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { errorMessage } from '../lib/api';

interface Notice {
  key: number;
  severity: 'success' | 'error' | 'info';
  message: string;
}

interface Notify {
  success: (message: string) => void;
  info: (message: string) => void;
  /** Accepts a message or any thrown value. */
  error: (error: unknown) => void;
}

const NotifyContext = createContext<Notify | null>(null);

/** App-wide feedback messages, shown bottom-right. */
export function NotifyProvider({ children }: { children: ReactNode }) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const push = useCallback((severity: Notice['severity'], message: string) => {
    setNotice({ key: Date.now(), severity, message });
  }, []);

  const value = useMemo<Notify>(
    () => ({
      success: (m) => push('success', m),
      info: (m) => push('info', m),
      error: (e) => push('error', typeof e === 'string' ? e : errorMessage(e)),
    }),
    [push],
  );

  return (
    <NotifyContext.Provider value={value}>
      {children}
      <Snackbar
        key={notice?.key}
        open={Boolean(notice)}
        autoHideDuration={notice?.severity === 'error' ? 8000 : 4000}
        onClose={(_e, reason) => reason !== 'clickaway' && setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notice ? (
          <Alert severity={notice.severity} variant="filled" onClose={() => setNotice(null)}>
            {notice.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotifyContext.Provider>
  );
}

export function useNotify(): Notify {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error('useNotify must be used inside NotifyProvider');
  return ctx;
}
